<i18n>
  {
    "fr": {
      "details": "Détails",
      "stopover" : "Direct | 1 escale | {n} escales",
      "flights-not-compatible": "Ce vol n'est pas compatible avec la sélection.",
      "luggage-class": "option",
      "luggage-paying": "payant",
      "tt-luggage-class": "Ce vol ne contient pas de bagage,<br>mais vous pouvez en prendre en<br>choisissant un autre tarif.",
      "tt-luggage-paying": "Ce vol ne contient pas de bagage,<br>mais vous pouvez en prendre en<br>payant un supplément.",
      "tt-no-luggage": "Ce vol ne contient pas de bagage."
    },
    "en": {
      "details": "Details",
      "stopover" : "Direct | 1 stopover | {n} stopovers",
      "flights-not-compatible": "This flight is not compatible with the selection.",
      "luggage-class": "option",
      "luggage-paying": "paying",
      "tt-luggage-class": "There is no luggage on this flight,<br>but you can take it by choosing<br>another fare.",
      "tt-luggage-paying": "There is no luggage on this flight,<br>but you can take it for<br>an extra charge.",
      "tt-no-luggage": "There is no luggage on this flight."
    }
  }
</i18n>

<template>
  <div>
    <v-card
      flat
      tile
      class="my-0 my-auto"
    >
      <v-row no-gutters>
        <v-tooltip
          right
          :disabled="canSelected"
        >
          <template #activator="{ on, attrs }">
            <div
              v-bind="attrs"
              v-on="on"
            >
              <v-btn
                depressed
                icon
                color="primary"
                class="d-flex-inline mx-auto"
                :disabled="!canSelected"
                @click="$emit('toggle')"
              >
                <v-icon size="large">
                  {{ active ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-blank-circle-outline' }}
                </v-icon>
              </v-btn>
            </div>
          </template>
          <span>{{ $t('flights-not-compatible') }}</span>
        </v-tooltip>

        <v-col
          cols="2"
          class="my-auto"
        >
          <v-img
            :src="getLogo"
            :height="$vuetify.breakpoint.lgAndUp ? 35 : 'auto'"
            eager
            contain
          />
        </v-col>
        <v-col
          cols="4"
          class="my-auto text-center"
        >
          {{ formatHour(flight.begin) }}
          <strong>{{ flight.from_place.code }}</strong>
          <span><v-icon color="accent">mdi-arrow-right</v-icon></span>
          {{ formatHour(flight.end) }}
          <sup
            v-if="arrivalIsNotTheSameDay(flight)"
            class="red--text"
          >+1 </sup>
          <strong>{{ flight.to_place.code }}</strong>
        </v-col>
        <v-col
          cols="2"
          class="my-auto text-center"
        >
          {{ formatDuration(flight.duration) }}
        </v-col>
        <v-col
          cols="1"
          class="my-auto text-center text-no-wrap"
        >
          {{ hasStopOver(flight.segments) }}
        </v-col>
        <v-col
          class="my-auto text-center"
        >
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
              >
                <v-icon
                  v-if="flight.price_included_luggage === 'YES'"
                  color="primary"
                >
                  mdi-bag-carry-on
                </v-icon>
                <v-icon
                  v-else-if="flight.price_included_luggage === 'OPTIONAL'"
                  color="dark-grey"
                >
                  mdi-bag-carry-on-off
                </v-icon>
                <v-icon
                  v-else
                  color="grey"
                >
                  mdi-bag-carry-on-off
                </v-icon>
                <small v-if="flight.included_luggage === 'OPTIONAL' && flight.price_included_luggage === 'NO'"><br>{{ $t('luggage-class') }}</small>
                <small v-if="flight.included_luggage === 'OPTIONAL' && flight.price_included_luggage !== 'NO'"><br>{{ $t('luggage-paying') }}</small>
              </div>
            </template>
            <span
              v-if="flight.included_luggage === 'OPTIONAL' && flight.price_included_luggage === 'NO'"
              class="d-inline-block text-center"
              v-html="$t('tt-luggage-class')"
            />
            <span
              v-if="flight.included_luggage === 'OPTIONAL' && flight.price_included_luggage !== 'NO'"
              class="d-inline-block text-center"
              v-html="$t('tt-luggage-paying')"
            />
            <span
              v-if="flight.included_luggage === 'NO'"
              class="d-inline-block text-center"
              v-html="$t('tt-no-luggage')"
            />
          </v-tooltip>
        </v-col>
        <v-col
          class="my-auto"
          justify-content="end"
        >
          <v-btn
            color="accent"
            class="px-0"
            dark
            text
            small
            @click="$emit('change-open', index)"
          >
            <b>{{ $t("details") }}</b>
            <v-icon
              v-if="isOpenPanel"
            >
              mdi-menu-up
            </v-icon>
            <v-icon
              v-else-if="!isOpenPanel"
            >
              mdi-menu-down
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'FlightItinerary',
  props: {
    flight: {
      type: Object,
      required: false,
      default: null
    },
    index: {
      type: Number,
      required: false,
      default: 0
    },
    openedPanel: {
      type: Number,
      required: true,
      default: -1
    },
    canSelected: Boolean,
    active: Boolean
  },
  computed: {
    /**
     * Is used to modify the icon of the button that open the expansion panel in the template
     * @return {boolean}
     */
    isOpenPanel () {
      return this.index === this.openedPanel
    },
    getLogo () {
      try {
        return require(`@/assets/img/airlines-logos/${this.flight.segments[0].marketing_carrier}.png`)
      } catch (e) {
        return require('@/assets/img/airlines-logos/unknow.png')
      }
    }
  },
  methods: {
    /**
     * Format raw time to HH:mm format
     * @param rawTime {string}
     * @return {string}
     */
    formatHour (rawTime) {
      const hour = moment(rawTime)
      return hour.format('HH:mm')
    },
    /**
     * Format duration to 00H00m format
     * @param rawDuration {number}
     * @return {string}
     */
    formatDuration (rawDuration) {
      const momentDuration = moment.duration(rawDuration, 'minutes')
      const hours = Math.floor(momentDuration.asHours())
      let minutes = (momentDuration.minutes())
      if (minutes < 10) {
        minutes = `0${minutes}`
      }
      return `${hours}H${minutes}m`
    },
    /**
     * Take an array of segment objects as an input and return a traduction based on the length of the array
     * @param segments {Object[]}
     * @return {string}
     */
    hasStopOver (segments) {
      return this.$i18n.tc('stopover', segments.length - 1)
    },
    /**
     * Take a flight as an input and check if the begining is the same day as the end
     * @param flight {Object}
     * @return {boolean}
     */
    arrivalIsNotTheSameDay (flight) {
      const departure = this.$moment(flight.begin)
      const arrival = this.$moment(flight.end)
      return departure.format('DD') !== arrival.format('DD')
    }
  }
}
</script>

<style scoped>

</style>
